import { Tooltip } from "@twinkltech/curriculum-architect-component-library";
import {
  Button,
  IconNavigationMenuOpen,
  IconCommunicationInformation,
  Text,
  IconActionsAdd,
} from "@twinkltech/web-ui";
import { useState, useId, useRef } from "react";
import { useTranslation } from "react-i18next";

import { CustomUnitCreateModal } from "@/components/CustomUnitCreateModal/CustomUnitCreateModal";
import { Draggable } from "@/components/Draggable/Draggable";
import { Droppable } from "@/components/Droppable/Droppable";
import { NewDrawerCard } from "@/components/NewDrawerCard/NewDrawerCard";

import { useUrlFilters } from "@/hooks/useUrlFilters/useUrlFilters";

import { EDIT_SUBJECT_PLAN_NS } from "@/pages/EditSubjectPlan/EditSubjectPlan.i18n";

import {
  TableData,
  TableResource,
} from "@/states/unitTableReducer/unitTableReducer";

import { makeInert } from "@/utils/makeInert/makeInert";

import { UnitDrawerCardActionMenu } from "./components/UnitDrawerCardActionMenu/UnitDrawerCardActionMenu";

export const UnitDrawer = ({
  curriculumId,
  onAddCustomUnit,
  resources,
  subjectId,
  tableData,
}: {
  curriculumId: string;
  subjectId: string;
  resources: TableResource[];
  tableData: Pick<TableData, "cells" | "rows" | "columns">;
  onAddCustomUnit: (payload: {
    row: string;
    column: string;
    span?: number;
    title: string;
    description?: string;
  }) => void;
}) => {
  const { t } = useTranslation(EDIT_SUBJECT_PLAN_NS);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [isCustomUnitModalOpen, setIsCustomUnitModalOpen] = useState(false);
  const regionLabelId = useId();
  const drawerTriggerId = useId();

  // Make sure we can't interact or focus on the list when the drawer is closed
  makeInert({
    isInert: !isDrawerOpen,
    ref: listContainerRef,
  });

  // Get filter values from URL
  const { filteredResources } = useUrlFilters({
    filterKeys: ["keyStages", "threads"],
    resources,
  });

  return (
    <Droppable data={{ target: "drawer" }} id="drawer">
      <div
        aria-controls="unit-drawer-list"
        aria-expanded={isDrawerOpen}
        aria-labelledby={regionLabelId}
        className={[
          "grow pl-400 pb-400 flex flex-col transition-all duration-500 ease-in-out",
          isDrawerOpen && "w-[420px]",
          !isDrawerOpen && "w-[70px]",
        ]
          .filter(Boolean)
          .join(" ")}
        role="complementary"
      >
        <Button
          aria-labelledby={drawerTriggerId}
          className="mb-6 self-end mr-2 text-xl py-1 px-2"
          variant="ghost"
          onClick={() => setIsDrawerOpen(!isDrawerOpen)}
        >
          <IconNavigationMenuOpen
            className={["h-8 w-8", isDrawerOpen ? "rotate-180" : ""]
              .filter(Boolean)
              .join(" ")}
          />
          <span className="sr-only" id={drawerTriggerId}>
            {isDrawerOpen
              ? t("unit-drawer.collapse-drawer")
              : t("unit-drawer.expand-drawer")}
          </span>
        </Button>
        <div
          ref={listContainerRef}
          aria-atomic="true"
          aria-live="polite"
          aria-relevant="additions removals"
          className={[
            "transition-margin duration-300 ease-in-out whitespace-nowrap flex flex-col",
            isDrawerOpen ? "ml-0" : "ml-[100px]",
          ]
            .filter(Boolean)
            .join(" ")}
          id="unit-drawer-list"
          role="region"
        >
          <div className="mb-4">
            <Text as="h2" variant="lg-medium">
              <span id={regionLabelId}>{t("unit-drawer.title")}</span>
              <Tooltip
                description={t("unit-drawer.info-text")}
                tooltipClasses="p-1 text-sm"
              >
                <IconCommunicationInformation className="inline mr-025 ml-050 mb-1 w-4 h-4" />
              </Tooltip>
            </Text>
            <Text as="p" className="text-neutral-700" variant="base-normal">
              {}
            </Text>
          </div>

          {/* Resource count indicator */}
          <div className="mb-3 px-1">
            <Text as="p" className="text-neutral-600 text-sm">
              {t("unit-drawer.resource-count", {
                count: filteredResources?.length,
                total: resources.length,
              })}
            </Text>
          </div>

          <ul
            aria-label="Unit card list"
            className="flex flex-col gap-4 max-h-[calc(100vh-427px)] min-h-[150px] overflow-y-auto overflow-x-hidden"
          >
            {filteredResources?.map((resource, index) => {
              return (
                <li
                  key={resource.id}
                  aria-label="unit drawer item"
                  className="relative group"
                >
                  <Draggable
                    key={resource.id}
                    className="cursor-grab"
                    data={{
                      target: "drawer-item",
                      data: {
                        resourceId: resource.id,
                      },
                    }}
                    dragOverlayClassName="rotate-[5.2deg] max-w-[240px] w-full outline-2 outline-blue-500 shadow-lg"
                    id={`${resource.id}__${index}`}
                    render={({ isDragging }) => (
                      <NewDrawerCard
                        aria-label={`Unit drawer card: ${resource.name}`}
                        className={[
                          "flex justify-between w-[360px] border border-neutral-150 rounded-[8px] px-5 py-3 shadow-[0_1px_2px_0px_rgba(0,0,0,0.1)] hover:border-[#C5CAD3] group-hover:border-[#C5CAD3]",
                          isDragging && "bg-neutral-150!",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        isDragging={isDragging}
                        resource={resource}
                      />
                    )}
                  />

                  <div className="flex flex-col justify-center absolute left-[314px] top-1/2 transform -translate-y-1/2">
                    <UnitDrawerCardActionMenu
                      curriculumId={curriculumId}
                      resourceId={resource.id}
                      subjectId={subjectId}
                    />
                  </div>
                </li>
              );
            })}

            <li>
              <Button
                aria-labelledby="custom-unit-label"
                className={[
                  "flex items-center justify-between w-[360px] h-[84px] border-2 border-dashed border-neutral-150 rounded-lg px-5 py-3 active:border-blue-500 active:bg-white active:border-solid",
                  isCustomUnitModalOpen &&
                    "border-blue-500 bg-white border-solid",
                ]
                  .filter(Boolean)
                  .join(" ")}
                variant="ghost"
                onClick={() => setIsCustomUnitModalOpen(true)}
              >
                <Text as="p" id="custom-unit-label" variant="sm-medium">
                  Add a custom unit
                </Text>
                <IconActionsAdd />
              </Button>
              <CustomUnitCreateModal
                open={isCustomUnitModalOpen}
                tableData={tableData}
                title="Add Custom Unit"
                onClose={() => setIsCustomUnitModalOpen(false)}
                onSubmit={(payload) => onAddCustomUnit(payload)}
              />
            </li>
          </ul>
        </div>
      </div>
    </Droppable>
  );
};
